import React, { useState } from 'react';
import { Campaign } from '@/types/campaign';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import { RequirementsPreview } from './RequirementsPreview';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ChevronRight, CalendarClock, Briefcase, Package, MoreVertical, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { getCampaignRedirectUrl } from '@/utils/campaignRedirects';
import { apiClient } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';

interface CampaignCardProps {
  campaign: Campaign;
  packages?: any[]; // Packages available for this campaign
}

export const CampaignCard: React.FC<CampaignCardProps> = ({ campaign, packages = [] }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const hasPackages = packages && packages.length > 0 && packages.some(pkg => pkg.status === 'active');

  const handleViewDetails = () => {
    const redirectUrl = getCampaignRedirectUrl(campaign, hasPackages);
    navigate(redirectUrl);
  };

  const handleDeleteCampaign = async () => {
    setIsDeleting(true);
    try {
      // Update campaign status to 'cancelled' instead of deleting
      await apiClient.updateCampaign(campaign.id, { status: 'cancelled' });
      
      // Invalidate and refetch campaigns data
      queryClient.invalidateQueries({ queryKey: ['brandCampaigns'] });
      
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Prevent deletion when hold is placed or for certain statuses
  const canDelete = campaign.status !== 'hold' && !['live', 'completed'].includes(campaign.status);

  return (
    <Card className="group hover:border-neutral-900 transition-all duration-300 border-neutral-200 overflow-hidden flex flex-col h-full bg-white shadow-sm hover:shadow-md">
      <CardHeader className="pb-4 bg-white border-b border-neutral-100">
        <div className="flex justify-between items-start gap-4">
          <div className="space-y-1.5 flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <CardTitle className="text-lg font-bold text-neutral-900 truncate pr-2 group-hover:text-black transition-colors">
                {campaign.name}
              </CardTitle>
              <div className="flex items-center gap-2">
                <CampaignStatusBadge status={campaign.status} />
                {hasPackages && (
                  <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                    <Package className="h-3 w-3 mr-1" />
                    Package Available
                  </Badge>
                )}
              </div>
            </div>
            
            {campaign.window_details && (
              <CardDescription className="flex items-center gap-2 text-sm text-neutral-500">
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-neutral-50 border border-neutral-200 text-xs font-medium">
                  <CalendarClock className="w-3 h-3 text-yellow-600" />
                  <span className="truncate max-w-[150px]">{campaign.window_details.name}</span>
                </div>
                {campaign.window_details.is_active && (
                   <span className="flex h-2 w-2 rounded-full bg-yellow-500 ring-2 ring-yellow-100" />
                )}
              </CardDescription>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewDetails}
              className="text-neutral-400 hover:text-black hover:bg-neutral-50 shrink-0"
            >
              <span className="sr-only sm:not-sr-only sm:mr-1">Details</span>
              <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            
            {canDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-neutral-400 hover:text-black hover:bg-neutral-50 shrink-0"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Campaign
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-6 flex-1 flex flex-col">
        <div className="space-y-4 flex-1">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-400">
            <Briefcase className="w-3.5 h-3.5 text-yellow-600" />
            <span>Details</span>
          </div>
          <RequirementsPreview requirements={campaign.requirements} />
        </div>
        
        <div className="mt-6 pt-4 border-t border-neutral-100 flex items-center justify-end text-xs text-neutral-400 font-medium">
            <span>Created {format(new Date(campaign.created_at), 'MMM d, yyyy')}</span>
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Campaign</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{campaign.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCampaign}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Campaign'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
